"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, CircleCheck as CheckCircle2, Search, TrendingUp, ShieldCheck } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
export default function ConsultantHero() {
  const shouldReduceMotion = useReducedMotion();
  const trustHighlights = [{
    text: "Direct consultant access"
  }, {
    text: "Tailored search strategy"
  }, {
    text: "Practical technical delivery"
  }, {
    text: "Transparent reporting"
  }];
  const targetSectors = ["Law firms", "Accountancies", "Management consultancies", "B2B service firms"];
  return <section id="consultanthero" data-nav="light" className="relative overflow-hidden bg-background py-16 md:py-24 lg:py-28">
      {/* Decorative ambient background accents */}
      <div aria-hidden="true" className="pointer-events-none absolute -top-24 right-0 size-96 rounded-full bg-secondary/60 blur-3xl" />
      <div aria-hidden="true" className="pointer-events-none absolute bottom-0 left-1/4 size-80 rounded-full bg-primary/5 blur-3xl" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-16">
          {/* Left Column: Copy & CTAs */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          ease: "easeOut"
        }} className="lg:col-span-7 flex flex-col items-start">
            {/* Direct access tag */}
            <div className="mb-6 flex items-center gap-2">
              <Badge variant="outline" className="rounded-full border-primary/20 bg-secondary/40 px-3.5 py-1 text-xs font-medium text-foreground">
                <ShieldCheck className="size-3.5 text-primary" />
                <span>Freelance SEO consultant &bull; You work directly with me</span>
              </Badge>
            </div>

            {/* Main H1 */}
            <h1 className="font-serif text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl lg:text-5xl leading-[1.15]">
              Professional SEO consultant for sustainable search growth
            </h1>

            {/* Supporting description */}
            <p className="mt-6 max-w-2xl text-base text-muted-foreground sm:text-lg leading-relaxed">
              I help law firms, accountancies, and professional services attract high-value client enquiries through bespoke SEO strategy, clean technical structure, and organic visibility built to last.
            </p>

            {/* Action buttons */}
            <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full sm:w-auto">
              <Button asChild size="lg" className="rounded-md bg-primary text-primary-foreground hover:bg-primary/90 shadow-md font-medium px-6 py-6 text-base">
                <Link to="/ContactPage" className="inline-flex items-center justify-center gap-2">
                  <span>Book a Free SEO Consultation</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="rounded-md border-border bg-background hover:bg-muted text-foreground font-medium px-6 py-6 text-base shadow-xs">
                <Link to="/CaseStudiesPage" className="inline-flex items-center justify-center gap-2">
                  <span>Explore My Case Studies</span>
                </Link>
              </Button>
            </div>

            {/* Trust highlights checklist */}
            <div className="mt-10 pt-8 border-t border-border/80 w-full">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                No junior account managers &bull; No outsourced execution
              </p>
              <div className="grid grid-cols-2 gap-3 sm:gap-4 sm:grid-cols-4">
                {trustHighlights.map((item, i) => <div key={item.text} data-index={i} className="flex items-center gap-2 text-xs sm:text-sm font-medium text-foreground">
                    <CheckCircle2 className="size-4 shrink-0 text-primary" />
                    <span>{item.text}</span>
                  </div>)}
              </div>
            </div>
          </motion.div>

          {/* Right Column: Expert Profile & Credibility Card */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.15,
          ease: "easeOut"
        }} className="lg:col-span-5 relative">
            <div className="relative mx-auto max-w-md lg:max-w-none">
              {/* Subtle accent backdrop framing */}
              <div aria-hidden="true" className="absolute -inset-2 rounded-2xl bg-secondary/50 -rotate-1" />

              {/* Main Profile Card */}
              <div className="relative rounded-xl border border-border bg-card p-6 sm:p-8 shadow-xl">
                {/* Consultant photo & title */}
                <div className="flex items-center gap-4 sm:gap-5 pb-6 border-b border-border">
                  <div className="relative shrink-0">
                    <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_seo_consultant.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_seo_consultant.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_seo_consultant.webp-wvc-sizes" alt="Yassine Essaadi, independent freelance SEO consultant" className="size-20 sm:size-24 rounded-full object-cover ring-2 ring-primary/20 bg-muted" loading="lazy" decoding="async" />
                    <span className="absolute bottom-0 right-0 block size-4 rounded-full bg-emerald-500 ring-2 ring-card" />
                  </div>

                  <div>
                    <h2 className="font-serif text-lg sm:text-xl font-semibold text-card-foreground">
                      Yassine Essaadi
                    </h2>
                    <p className="text-sm font-medium text-primary">
                      Freelance SEO Consultant
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Swindon &bull; UK &amp; International Clients
                    </p>
                  </div>
                </div>

                {/* Practical focus area */}
                <div className="py-5 space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="rounded-md bg-secondary/80 p-2 text-primary shrink-0">
                      <Search className="size-4" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-card-foreground">
                        Organic Search &amp; AI Visibility
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5 leading-normal">
                        Technical SEO audits, commercial intent targeting, and AI Search Optimisation for modern search engines.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="rounded-md bg-secondary/80 p-2 text-primary shrink-0">
                      <TrendingUp className="size-4" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-card-foreground">
                        Tailored for professional sectors
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5 leading-normal">
                        Purpose-built search strategies focused strictly on verified business leads, not meaningless impression spikes.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Target client chips */}
                <div className="pt-4 border-t border-border">
                  <p className="text-xs font-medium text-muted-foreground mb-2.5">
                    Advising professional practices:
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {targetSectors.map((sector, idx) => <span key={sector} data-index={idx} className="inline-flex items-center rounded-md bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground">
                        {sector}
                      </span>)}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}